import { useState, useEffect, useRef, useCallback } from 'react';
import { KaijuId } from '@/lib/gameData';
import { getIntroSlideTexts, getIntroSlideImage, getKaijuStoryImage, getKaijuName, getKaijuImage, getIntroMusic, getIntroTexts } from '@/lib/gameDataOverrides';
import { Volume2, VolumeX } from 'lucide-react';
import { unlockAudio, getIntroAudioElement } from '@/lib/audioUnlock';

interface IntroSequenceProps {
  kaijuId: KaijuId;
  onSkip: () => void;
  onComplete: () => void;
  muted: boolean;
  onToggleMute: () => void;
}

type Step = 0 | 1 | 2 | 3 | 4 | 5;

const fadeOutAudio = (audio: HTMLAudioElement, duration = 1500) => {
  const steps = 30;
  const interval = duration / steps;
  const decrement = audio.volume / steps;
  const timer = setInterval(() => {
    audio.volume = Math.max(0, audio.volume - decrement);
    if (audio.volume <= 0) {
      clearInterval(timer);
      audio.pause();
    }
  }, interval);
  return timer;
};

const IntroSequence = ({ kaijuId, onSkip, onComplete, muted, onToggleMute }: IntroSequenceProps) => {
  const [ready, setReady] = useState(false);
  const [step, setStep] = useState<Step>(0);
  const [visible, setVisible] = useState(false);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const slideTexts = getIntroSlideTexts();
  const kaijuSelectionImage = getKaijuImage(kaijuId);
  const kaijuStoryImage = getKaijuStoryImage(kaijuId);
  const slide2Image = getIntroSlideImage(2);
  const slide3Image = getIntroSlideImage(3);
  const musicUrl = getIntroMusic();

  // Prefetch all slide images on mount
  useEffect(() => {
    [kaijuSelectionImage, kaijuStoryImage, slide2Image, slide3Image].forEach(src => {
      if (src) {
        const img = new Image();
        img.src = src;
      }
    });
  }, [kaijuSelectionImage, kaijuStoryImage, slide2Image, slide3Image]);

  const isTextStep = step === 0 || step === 4;
  const isFinalStep = step === 5;

  // 1s initial delay before showing content and starting music
  useEffect(() => {
    const delay = setTimeout(() => setReady(true), 600);
    return () => clearTimeout(delay);
  }, []);

  // Start music immediately on mount — reuse primed audio element
  useEffect(() => {
    if (!musicUrl) return;
    const primed = getIntroAudioElement();
    const audio = primed || new Audio(musicUrl);
    audio.loop = true;
    audio.volume = 1;
    audio.muted = muted;
    audioRef.current = audio;
    audio.currentTime = 0;
    audio.play().catch(() => {});
    return () => {
      audio.pause();
      audio.currentTime = 0;
      audioRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync mute state
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = fadeOutAudio(audioRef.current, 1500) as unknown as ReturnType<typeof setInterval>;
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
    if (fadeOutTimerRef.current) { clearTimeout(fadeOutTimerRef.current); fadeOutTimerRef.current = null; }
  }, []);

  const handleSkip = useCallback(() => {
    cleanupAudio();
    onSkip();
  }, [onSkip, cleanupAudio]);

  const handleComplete = useCallback(() => {
    cleanupAudio();
    onComplete();
  }, [onComplete, cleanupAudio]);


  // Fade in on step change, auto-advance all steps except final (only when ready)
  useEffect(() => {
    if (!ready) return;

    const fadeInDelay = setTimeout(() => setVisible(true), 50);

    if (!isFinalStep) {
      const holdTime = isTextStep ? 2500 : step === 1 ? 3500 : 5250;
      fadeOutTimerRef.current = setTimeout(() => setVisible(false), holdTime);
      autoTimerRef.current = setTimeout(() => {
        setStep(prev => (prev + 1) as Step);
      }, holdTime + 1000);
    }

    return () => {
      clearTimeout(fadeInDelay);
      clearTimers();
    };
  }, [step, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSlideImage = (): string | null => {
    if (step === 1) return kaijuSelectionImage;
    if (step === 2) return kaijuStoryImage;
    if (step === 3) return slide2Image;
    if (step === 5) return slide3Image;
    return null;
  };

  const getSlideCaption = (): string => {
    const raw = step === 2 ? (slideTexts[0] || '') : step === 3 ? (slideTexts[1] || '') : step === 5 ? (slideTexts[2] || '') : '';
    const displayName = getKaijuName(kaijuId).name;
    return raw.replace(/\[insert kaiju\]/gi, displayName);
  };

  const introTexts = getIntroTexts();
  const textContent = step === 0 ? introTexts.teaser : introTexts.bridge;

  return (
    <div className="h-full flex flex-col relative">
      {/* Mute toggle */}
      {musicUrl && (
        <button
          onClick={onToggleMute}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg border border-border bg-background/80 backdrop-blur-sm hover:bg-muted/50 transition-colors"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX className="h-5 w-5 text-muted-foreground" /> : <Volume2 className="h-5 w-5 text-foreground" />}
        </button>
      )}

      {/* Content area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden px-4">
        {isTextStep ? (
          <div className="max-w-2xl w-full transition-opacity duration-700 ease-in-out" style={{ opacity: visible ? 1 : 0 }}>
            <h1
              className="text-2xl md:text-4xl font-pixel text-foreground text-center uppercase tracking-widest"
              style={{ textShadow: '3px 3px 0px hsl(var(--primary) / 0.3)' }}
            >
              {textContent}
            </h1>
          </div>
        ) : (
          <div
            className="max-w-2xl w-full transition-opacity duration-700 ease-in-out"
            style={{ opacity: visible ? 1 : 0 }}
          >
            {getSlideImage() && (
              <>
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-border">
                  <img
                    src={getSlideImage()!}
                    alt={`Intro slide ${step}`}
                    className="w-full h-full object-cover"
                  />
                  {getSlideCaption() && (
                    <div className="hidden md:block absolute bottom-4 left-4 right-4 bg-white border-[3px] border-black rounded px-4 py-3">
                      <p className="text-black font-mono-game text-center text-sm md:text-base leading-relaxed">
                        {getSlideCaption()}
                      </p>
                    </div>
                  )}
                </div>
                {getSlideCaption() && (
                  <div className="md:hidden mt-3 bg-white border-[3px] border-black rounded px-4 py-3">
                    <p className="text-black font-mono-game text-center text-sm leading-relaxed">
                      {getSlideCaption()}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer buttons */}
      <div className="shrink-0 border-t border-border bg-background/95 backdrop-blur-sm px-4 pt-4 pb-8 shadow-[0_-4px_20px_rgba(0,0,0,0.3)]">
        <div className="max-w-xl mx-auto">
          <button
            onClick={() => { unlockAudio(); (isFinalStep ? handleComplete : handleSkip)(); }}
            className={`w-full py-4 rounded-lg font-display text-lg font-bold uppercase tracking-wider transition-all ${
              isFinalStep
                ? 'bg-primary text-primary-foreground hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] hover:scale-[1.02]'
                : 'border border-border text-muted-foreground hover:bg-muted/50'
            }`}
          >
            {isFinalStep ? 'Next →' : 'Skip Intro'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntroSequence;
